import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { Badge, Button, Card, Table } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { getCurrentPeriodTicket } from '../../../controller/api/gamePeriod'
import { disableTicketRoll, enableTicketRoll, getClassicRollByRegion, getTicketRollStatus } from '../../../controller/api/systemApi'

const VIETLOTT_GAMES = [
  { key: 'lotto535', name: 'LOTO 5/35', single: 'isRollLotto535Enabled', group: 'isRollGroupLotto535Enabled', hasGroup: true },
  { key: 'keno', name: 'Keno', single: 'isRollKenoEnabled', hasGroup: false },
  { key: 'mega', name: 'Mega 6/45', single: 'isRollMegaEnabled', group: 'isRollGroupMegaEnabled', hasGroup: true },
  { key: 'power', name: 'Power 6/55', single: 'isRollPowerEnabled', group: 'isRollGroupPowerEnabled', hasGroup: true },
  { key: 'max3d', name: 'Max3D', single: 'isRollMax3dEnabled', hasGroup: false },
  { key: 'max3dpro', name: 'Max3D Pro', single: 'isRollMax3dProEnabled', hasGroup: false },
]

function ToggleCell({ enabled, pending, onToggle }) {
  if (typeof enabled !== 'boolean') {
    return <span className="text-muted">—</span>
  }

  return (
    <div className="d-flex flex-column align-items-center gap-2 py-2">
      <Badge bg={enabled ? 'success' : 'danger'}>
        {enabled ? 'Đang bật' : 'Đang tắt'}
      </Badge>
      <Button
        variant={enabled ? 'outline-danger' : 'outline-success'}
        size="sm"
        disabled={pending}
        onClick={onToggle}
      >
        {enabled ? 'Tắt chia vé' : 'Bật chia vé'}
      </Button>
    </div>
  )
}

export default function TicketRoll() {
  const [cookies] = useCookies(['lottmekong'])
  const [regions, setRegions] = useState([])
  const [lotoEnabled, setLotoEnabled] = useState(null)
  const [vietlott, setVietlott] = useState({})
  const [loading, setLoading] = useState(false)
  const [pendingKey, setPendingKey] = useState('')

  const loadStatus = useCallback(async () => {
    setLoading(true)
    const token = cookies.lottmekong
    const [classicResult, lotoResult, vietlottResult] = await Promise.allSettled([
      getClassicRollByRegion(token),
      getTicketRollStatus(token, '', false, true),
      getCurrentPeriodTicket(token),
    ])

    if (classicResult.status === 'fulfilled') {
      setRegions(Array.isArray(classicResult.value) ? classicResult.value : [])
    } else {
      setRegions([])
    }

    if (lotoResult.status === 'fulfilled') {
      setLotoEnabled(Boolean(lotoResult.value))
    } else {
      setLotoEnabled(null)
    }

    if (vietlottResult.status === 'fulfilled') {
      setVietlott(vietlottResult.value || {})
    } else {
      setVietlott({})
    }

    if ([classicResult, lotoResult, vietlottResult].some((item) => item.status === 'rejected')) {
      toast.error('Không tải đủ trạng thái chia vé')
    }
    setLoading(false)
  }, [cookies.lottmekong])

  useEffect(() => {
    document.title = 'Chia vé'
    loadStatus()
  }, [loadStatus])

  const runToggle = async (key, action) => {
    setPendingKey(key)
    try {
      await action()
      toast.success('Đã cập nhật chia vé')
      await loadStatus()
    } catch (error) {
      toast.error('Không đổi được trạng thái chia vé')
    } finally {
      setPendingKey('')
    }
  }

  const vietlottRows = useMemo(() => VIETLOTT_GAMES.map((game) => ({
    ...game,
    singleEnabled: typeof vietlott[game.single] === 'boolean' ? vietlott[game.single] : null,
    groupEnabled: game.hasGroup && typeof vietlott[game.group] === 'boolean' ? vietlott[game.group] : null,
  })), [vietlott])

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Chia vé</h4>
          <div className="small text-muted">Bật hoặc tắt chia vé cho kiến thiết, lô tô và Vietlott.</div>
        </div>
        <Button variant="outline-primary" size="sm" onClick={loadStatus} disabled={loading}>
          Làm mới
        </Button>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header>
          <h5 className="mb-1">Kiến thiết</h5>
          <div className="small text-muted">Mỗi miền bật tắt riêng. Đơn của miền đang tắt không được đưa vào hàng đợi in.</div>
        </Card.Header>
        <Card.Body className="pt-2">
          <Table responsive className="align-items-center table-bordered table-hover mb-0">
            <thead className="text-center">
              <tr>
                <th>Miền</th>
                <th>Chia vé</th>
              </tr>
            </thead>
            <tbody>
              {regions.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center text-muted">
                    {loading ? 'Đang tải...' : 'Chưa có dữ liệu'}
                  </td>
                </tr>
              ) : regions.map((item) => (
                <tr key={item.region}>
                  <td className="text-center fw-bold">{item.label || item.region}</td>
                  <td className="text-center">
                    <ToggleCell
                      enabled={Boolean(item.enabled)}
                      pending={pendingKey === `classic:${item.region}`}
                      onToggle={() => runToggle(`classic:${item.region}`, () => (
                        item.enabled
                          ? disableTicketRoll(cookies.lottmekong, '', true, false, false, item.region)
                          : enableTicketRoll(cookies.lottmekong, '', true, false, false, item.region)
                      ))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Header>
          <h5 className="mb-1">Lô tô</h5>
        </Card.Header>
        <Card.Body className="pt-2">
          <Table responsive className="align-items-center table-bordered table-hover mb-0">
            <thead className="text-center">
              <tr>
                <th>Sản phẩm</th>
                <th>Chia vé</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center fw-bold">Lô tô</td>
                <td className="text-center">
                  <ToggleCell
                    enabled={lotoEnabled}
                    pending={pendingKey === 'loto'}
                    onToggle={() => runToggle('loto', () => (
                      lotoEnabled
                        ? disableTicketRoll(cookies.lottmekong, '', false, true)
                        : enableTicketRoll(cookies.lottmekong, '', false, true)
                    ))}
                  />
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm">
        <Card.Header>
          <h5 className="mb-1">Vietlott</h5>
          <div className="small text-muted">Mua chung áp dụng với LOTO 5/35, Mega 6/45 và Power 6/55.</div>
        </Card.Header>
        <Card.Body className="pt-2">
          <Table responsive className="align-items-center table-bordered table-hover mb-0">
            <thead className="text-center">
              <tr>
                <th>Trò chơi</th>
                <th>Chia vé</th>
                <th>Chia vé mua chung</th>
              </tr>
            </thead>
            <tbody>
              {vietlottRows.map((row) => (
                <tr key={row.key}>
                  <td className="text-center fw-bold">{row.name}</td>
                  <td className="text-center">
                    <ToggleCell
                      enabled={row.singleEnabled}
                      pending={pendingKey === `vietlott:${row.key}`}
                      onToggle={() => runToggle(`vietlott:${row.key}`, () => (
                        row.singleEnabled
                          ? disableTicketRoll(cookies.lottmekong, row.key, false, false, false)
                          : enableTicketRoll(cookies.lottmekong, row.key, false, false, false)
                      ))}
                    />
                  </td>
                  <td className="text-center">
                    {row.hasGroup ? (
                      <ToggleCell
                        enabled={row.groupEnabled}
                        pending={pendingKey === `vietlott:${row.key}:group`}
                        onToggle={() => runToggle(`vietlott:${row.key}:group`, () => (
                          row.groupEnabled
                            ? disableTicketRoll(cookies.lottmekong, row.key, false, false, true)
                            : enableTicketRoll(cookies.lottmekong, row.key, false, false, true)
                        ))}
                      />
                    ) : (
                      <span className="text-muted">Không áp dụng</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  )
}
